/**
 * Login Form Component
 *
 * OAuth sign-in buttons for Google and Microsoft providers.
 * Handles session detection and post-login redirect.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SYNC: Documented in SYSTEM_DESCRIPTION.md §1 "Authentication & Authorization" │
 * │ If this code changes, update the documentation and references.         │
 * └─────────────────────────────────────────────────────────────────────────┘
 */
"use client";
import Button from "@/components/ui/Button";
import { signIn, useSession } from "next-auth/react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { GoogleIcon } from "../../atoms/GoogleIcon";
import { MicrosoftIcon } from "../../atoms/MicrosoftIcon";
import { UsernameIcon } from "../../atoms/UsernameIcon";
import { clearAuthCookies } from "../clear-auth-cookies";
import { USERNAME_LOGIN_DISABLED_CODE } from "@/client-constants";
import styles from "./LoginForm.module.scss";

const HOME_CALLBACK_URL = "/";

function isEmbeddedCodeBrowserUserAgent(userAgent: string): boolean {
  return userAgent.includes("Electron/") && userAgent.includes("Code/");
}

export default function LoginForm() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const hasRefreshedAfterAuth = useRef(false);
  const [embeddedBrowserUrl, setEmbeddedBrowserUrl] = useState<string | null>(null);
  const [loadingProvider, setLoadingProvider] = useState<
    "google" | "microsoft" | "credentials" | null
  >(null);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [credentialError, setCredentialError] = useState<string | null>(null);

  useEffect(() => {
    if (
      typeof navigator !== "object" ||
      typeof window !== "object" ||
      !isEmbeddedCodeBrowserUserAgent(navigator.userAgent)
    ) {
      return;
    }

    setEmbeddedBrowserUrl(window.location.origin);
  }, []);

  const isEmbeddedCodeBrowser = embeddedBrowserUrl !== null;

  // Refresh the home route once the browser has the session cookie so the
  // server-side `/` redirect can choose the correct destination.
  useEffect(() => {
    if (status !== "authenticated" || !session || hasRefreshedAfterAuth.current) {
      return;
    }

    hasRefreshedAfterAuth.current = true;
    router.refresh();
  }, [session, status, router]);

  const handleGoogleSignIn = async () => {
    if (isEmbeddedCodeBrowser) {
      return;
    }

    clearAuthCookies();
    setLoadingProvider("google");
    try {
      await signIn("google", {
        callbackUrl: HOME_CALLBACK_URL,
        redirect: true,
      });
    } catch (error) {
      console.error("Sign in failed:", error);
      setLoadingProvider(null);
    }
  };

  const handleMicrosoftSignIn = async () => {
    if (isEmbeddedCodeBrowser) {
      return;
    }

    clearAuthCookies();
    setLoadingProvider("microsoft");
    try {
      await signIn("azure-ad", {
        callbackUrl: HOME_CALLBACK_URL,
        redirect: true,
      });
    } catch (error) {
      console.error("Sign in failed:", error);
      setLoadingProvider(null);
    }
  };

  const handleUsernameSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCredentialError(null);
    setLoadingProvider("credentials");
    try {
      const result = await signIn("credentials", {
        username,
        password,
        redirect: false,
      });
      if (!result || result.error) {
        setCredentialError(
          (result?.error ?? "").includes(USERNAME_LOGIN_DISABLED_CODE)
            ? "Username login not activated for your account, please contact support if you want to enable it."
            : "Invalid username or password."
        );
        setLoadingProvider(null);
        return;
      }
      // Success — let the server-side `/` route choose the destination (forced
      // password change / pending invitations / dashboard).
      router.push("/");
      router.refresh();
    } catch (error) {
      console.error("Username sign in failed:", error);
      setCredentialError("Sign in failed. Please try again.");
      setLoadingProvider(null);
    }
  };

  if (status === "loading") {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (session) {
    return (
      <div className={styles.container}>
        <div className={styles.redirecting}>Redirecting to dashboard...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Image
          src="/site-images/Segmental-no-background.png"
          alt="Segmental logo"
          width={250}
          height={250}
          className="mx-auto rounded-full"
          style={{ marginBottom: "-50px" }}
        />

        <p style={{ textAlign: "center", color: "#ccc", fontSize: "12px" }}>by</p>
        <Image
          src="/site-images/7030-digital.png"
          alt="7030 Digital logo"
          width={100}
          height={55}
          className="mx-auto my-4 rounded-full"
          style={{ marginBottom: "16px" }}
        />
        {isEmbeddedCodeBrowser && (
          <div className={styles.embeddedBrowserNotice} role="alert">
            Google and Microsoft sign-in do not complete in the VS Code preview browser.
            Open {embeddedBrowserUrl} in your system browser and sign in there.
          </div>
        )}
        <Button
          onClick={
            loadingProvider || isEmbeddedCodeBrowser ? undefined : handleGoogleSignIn
          }
          aria-disabled={isEmbeddedCodeBrowser}
          tabIndex={isEmbeddedCodeBrowser ? -1 : undefined}
          className={`${styles.googleButton} ${isEmbeddedCodeBrowser ? styles.blockedButton : ""}`}
        >
          <GoogleIcon />
          {loadingProvider === "google" ? "Signing in..." : "Sign in with Google"}
        </Button>
        <Button
          onClick={
            loadingProvider || isEmbeddedCodeBrowser ? undefined : handleMicrosoftSignIn
          }
          aria-disabled={isEmbeddedCodeBrowser}
          tabIndex={isEmbeddedCodeBrowser ? -1 : undefined}
          className={`${styles.microsoftButton} ${isEmbeddedCodeBrowser ? styles.blockedButton : ""}`}
        >
          <MicrosoftIcon />
          {loadingProvider === "microsoft" ? "Signing in..." : "Sign in with Microsoft"}
        </Button>

        <Button
          onClick={() => {
            setCredentialError(null);
            setShowUsernameForm((open) => !open);
          }}
          aria-expanded={showUsernameForm}
          className={styles.usernameButton}
        >
          <UsernameIcon />
          Sign in with Username
        </Button>

        {showUsernameForm && (
          <form
            onSubmit={handleUsernameSignIn}
            style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}
          >
            <input
              type="text"
              name="username"
              autoComplete="username"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={credentialInputStyle}
            />
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={credentialInputStyle}
            />
            {credentialError && (
              <div role="alert" style={{ color: "#ff8080", fontSize: 13 }}>
                {credentialError}
              </div>
            )}
            <button
              type="submit"
              disabled={loadingProvider === "credentials"}
              style={{
                height: 44,
                borderRadius: 4,
                border: "none",
                background: loadingProvider === "credentials" ? "#555" : "#4f7cff",
                color: "#fff",
                fontWeight: 600,
                cursor: loadingProvider === "credentials" ? "default" : "pointer",
              }}
            >
              {loadingProvider === "credentials" ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const credentialInputStyle: React.CSSProperties = {
  height: 44,
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: 4,
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(0,0,0,0.25)",
  color: "#fff",
  fontSize: 14,
};
